import { FC, useMemo, useState } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'

import {
  useCreateSoapSetMutation,
  useDeleteSoapSetMutation,
  useSoapMasters,
  useUpdateSoapSetMutation,
  type SoapSet,
  type SoapSetInput,
} from '@/features/soap-masters'
import {
  Banner,
  ConfirmDialog,
  EmptyState,
  NotionButton,
  NotionTextField,
  PageLoading,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import { SoapSetFormDialog } from '@/apps/latest/components/organisms/SoapMasters/SoapSetFormDialog'
import { SoapSetLines } from '@/apps/latest/components/organisms/SoapMasters/SoapSetLines'
import {
  authorsOf,
  filterSoapSets,
} from '@/apps/latest/components/organisms/SoapMasters/soapSetFilter'
import { soapSetErrorMessage } from '@/apps/latest/components/organisms/SoapMasters/soapSetError'
import {
  soapSetLabel,
  soapSetMeta,
} from '@/apps/latest/components/organisms/SoapMasters/soapSetLabel'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { DURATION } from '@/shared/styles/motion'
import { useToast } from '@/shared/contexts/ToastContext'

// 定型文を**整える**面。薬歴で使う面 (挿入ダイアログ) とは分けてある。
//
// - 店舗の共有資産なので、ここで直すと同じ店舗の全員に効く
// - 詳細ページは作らない。作成・編集はダイアログ 1 つ (ADR-0017)
// - 進捗 (区分が未設定の残数) は画面に出さない。常設カウンタは置かない規約
//
// 設計正本: hub log/80 work-log/007

// 行の操作は hover / focus で出す (常設すると一覧が操作子で埋まる)。
const rowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  px: 1,
  py: 1,
  borderRadius: `${RADIUS.md}px`,
  transition: `background-color ${DURATION.fast}ms`,
  '&:hover': { bgcolor: colorTokens.overlay.hover },
  '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
}

export const SoapMastersPage: FC = () => {
  const { soapSets, isLoading, error } = useSoapMasters()
  const createMutation = useCreateSoapSetMutation()
  const updateMutation = useUpdateSoapSetMutation()
  const deleteMutation = useDeleteSoapSetMutation()
  const { showToast } = useToast()

  const [query, setQuery] = useState('')
  const [author, setAuthor] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<SoapSet | null>(null)
  const [deleting, setDeleting] = useState<SoapSet | null>(null)

  const authors = useMemo(() => authorsOf(soapSets), [soapSets])

  const visible = useMemo(
    () => filterSoapSets(soapSets, { query, author }),
    [soapSets, author, query]
  )

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (soapSet: SoapSet) => {
    setEditing(soapSet)
    setFormOpen(true)
  }

  const handleSubmit = async (input: SoapSetInput) => {
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, input })
        showToast('定型文を更新しました', 'success')
      } else {
        await createMutation.mutateAsync(input)
        showToast('定型文を追加しました', 'success')
      }
      setFormOpen(false)
      setEditing(null)
    } catch (err) {
      // back の検証文言をそのまま出す。front で先に弾けるのは 2 つだけで、
      // 残り (桁あふれ等) は「保存に失敗しました」だけだと直しようが無い。
      showToast(soapSetErrorMessage(err, '保存に失敗しました'), 'error')
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteMutation.mutateAsync(deleting.id)
      showToast('定型文を削除しました', 'success')
    } catch (err) {
      showToast(soapSetErrorMessage(err, '削除に失敗しました'), 'error')
    } finally {
      setDeleting(null)
    }
  }

  if (isLoading) {
    return (
      <PageScaffold title="定型文">
        <PageLoading />
      </PageScaffold>
    )
  }

  return (
    <PageScaffold
      title="定型文"
      meta={soapSets.length > 0 ? `全 ${soapSets.length} 件` : undefined}
      actions={
        <NotionButton variant="primary" onClick={openCreate}>
          <AddIcon fontSize="small" />
          新規
        </NotionButton>
      }
    >
      <Stack gap={2}>
        {error && <Banner severity="danger">定型文の取得に失敗しました</Banner>}

        {/* 取得に失敗しているときは空状態も一覧も出さない。「0 件だから作りましょう」と
            「読めていない」は別物で、並べると読めなかった店に新規登録を勧めることになる。 */}
        {!error &&
          (soapSets.length === 0 ? (
            <EmptyState
              icon={<TextSnippetIcon />}
              title="定型文がありません"
              hint="よく使う文言を登録すると、薬歴の編集中に選んで挿入できます。"
              action={
                <NotionButton variant="primary" onClick={openCreate}>
                  <AddIcon fontSize="small" />
                  新規
                </NotionButton>
              }
            />
          ) : (
            <>
              <NotionTextField
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="検索 (分類・見出し・作成者・本文)"
              />

              {/* 作成者は「自分の定型文」を出す軸。分類・見出しは検索で当たるので chips を置かない。 */}
              {authors.length >= 2 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {[null, ...authors].map((name) => (
                    <Box
                      key={name ?? '__all__'}
                      component="button"
                      type="button"
                      onClick={() => setAuthor(name)}
                      sx={{
                        appearance: 'none',
                        border: 'none',
                        fontFamily: 'inherit',
                        ...PORTAL_TEXT.badge,
                        minHeight: 24,
                        px: 1,
                        borderRadius: `${RADIUS.full}px`,
                        cursor: 'pointer',
                        color:
                          author === name
                            ? colorTokens.fg.default
                            : colorTokens.fg.muted,
                        bgcolor:
                          author === name
                            ? colorTokens.overlay.selected
                            : 'transparent',
                        transition: `background-color ${DURATION.fast}ms`,
                        // hover は非選択にのみ当てる。選択中にも一律で当てると
                        // overlay.hover (4%) が overlay.selected (6%) を上書きして
                        // 選択中が薄く見える (design-book 02-components/tab-pill.md Don't)。
                        '&:hover': {
                          bgcolor:
                            author === name
                              ? colorTokens.overlay.selected
                              : colorTokens.overlay.hover,
                        },
                        '&:focus': { outline: 'none' },
                        '&:focus-visible': {
                          boxShadow: colorTokens.focus.ringNeutral,
                        },
                      }}
                    >
                      {name ?? 'すべて'}
                    </Box>
                  ))}
                </Box>
              )}

              <Stack gap={0.25}>
                {visible.map((s) => (
                  <Box key={s.id} sx={rowSx}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 0.75,
                        }}
                      >
                        <Typography sx={PORTAL_TEXT.cellBody}>
                          {soapSetLabel(s) || '(見出しなし)'}
                        </Typography>
                        <Typography sx={PORTAL_TEXT.cellMeta}>
                          {soapSetMeta(s)}
                        </Typography>
                      </Box>
                      <SoapSetLines lines={s.lines} scaled={false} />
                    </Box>
                    <Stack
                      className="row-actions"
                      direction="row"
                      gap={0.5}
                      sx={{
                        opacity: 0,
                        transition: `opacity ${DURATION.fast}ms`,
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label="編集"
                        onClick={() => openEdit(s)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="削除"
                        onClick={() => setDeleting(s)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                ))}
                {visible.length === 0 && (
                  <Typography sx={{ ...PORTAL_TEXT.empty, px: 1, py: 2 }}>
                    (該当なし)
                  </Typography>
                )}
              </Stack>
            </>
          ))}
      </Stack>

      {/* 開くたびに mount し直す (= 前回の編集途中を持ち越さない)。 */}
      {formOpen && (
        <SoapSetFormDialog
          key={`soap-set-form-${editing?.id ?? 'new'}`}
          onClose={() => {
            setFormOpen(false)
            setEditing(null)
          }}
          target={editing}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}

      {/* 削除は非可逆なので confirm を挟む (追記の挿入とは扱いが違う)。 */}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => void handleDelete()}
        title="定型文を削除"
        message={`「${
          deleting ? soapSetLabel(deleting) || deleting.lines[0]?.content : ''
        }」を削除します。元に戻せません。`}
        confirmLabel="削除"
        variant="danger"
        disabled={deleteMutation.isPending}
      />
    </PageScaffold>
  )
}

export default SoapMastersPage
